/*
 * The refusal line: why nothing happened (chunk U-0's split of `panels.ts`).
 *
 * The one surface in this client that says the world did *not* change. Its two
 * constants live here rather than beside the dialogue line's because they are a
 * different judgement: a refusal is read and forgotten, and a spoken line is
 * read.
 */

/**
 * How long a refusal stays up, and how long it takes to go.
 *
 * PROVISIONAL, and deliberately half the spoken line's hold: *"nothing to gather
 * here"* is four words with nothing to check them against, and a line that
 * outstayed the moment the player pressed the key would start reading as a
 * complaint about the *next* thing they did. Short enough to be a flicker of
 * feedback, long enough to be read at a glance.
 */
const FEEDBACK_HOLD_SECONDS = 2.4;
const FEEDBACK_FADE_SECONDS = 0.8;

/**
 * Why nothing happened: one short line under the crosshair, and then gone.
 *
 * **The wave-1.5 graybox ruling** (`docs/plans/phase-7-plan.md`): *every refused
 * action displays a judgeable message*. The playtest that asked for it found `G`
 * outside town doing nothing at all with no message, which is indistinguishable
 * from a broken key — and a player who cannot tell a refusal from a bug stops
 * pressing.
 *
 * Two things about it are the ruling rather than taste:
 *
 * - **The simulation stays silent and authoritative.** A tick has no error
 *   channel and is not getting one (`sim_core::Command::Dig`'s rule): commands
 *   are data submitted a frame ago, and a `Result` out of `tick` would describe
 *   a batch rather than a command. So this line is *predicted* client-side from
 *   reads the client already has — the tile under the crosshair, the board, how
 *   many things are in the player's hands — and never reported back by the
 *   world.
 * - **Where the client cannot predict, it says so.** A guess dressed as a
 *   diagnosis is worse than *"nothing happened"*, because the first one teaches
 *   a player something false about the world and the second one only tells them
 *   what they already saw (`witness/witness.ts`'s `refuse`).
 *
 * A prompt and not a panel: no backdrop, no key to dismiss, nothing to read. It
 * takes the third prompt slot's shape and its own place on screen — under the
 * aiming prompt, which is where the player is already looking when they press
 * the key that got refused.
 */
export class FeedbackLine {
  private readonly element: HTMLElement;

  /** Seconds this line has left before it starts fading, then while it fades. */
  private remaining = 0;

  /** What is up, so an unchanged frame writes nothing to the DOM. */
  private shown: string | null = null;

  constructor(root: HTMLElement) {
    this.element = document.createElement("div");
    // The kit's shadow for type drawn over the world, composed alongside the
    // client's own name (plan D2). The line is set in serif where the prompt
    // above it is sans, because it is a sentence and that one is a key legend
    // (`ui/hud.css`'s register note).
    this.element.className = "hud__feedback hud-text";
    this.element.hidden = true;
    root.append(this.element);
  }

  /** Whether a line is up. Read by the tests and by nothing in the loop. */
  get open(): boolean {
    return this.shown !== null;
  }

  /**
   * Says why nothing happened, restarting the fade.
   *
   * The same text twice in a row restarts the hold rather than being ignored: a
   * player mashing `G` at a rock is asking the same question again, and a line
   * that faded out under them would look like the client had given up.
   */
  say(text: string): void {
    this.remaining = FEEDBACK_HOLD_SECONDS + FEEDBACK_FADE_SECONDS;

    if (text === this.shown) {
      this.element.style.opacity = "1";

      return;
    }

    this.shown = text;
    this.element.textContent = text;
    this.element.hidden = false;
    this.element.style.opacity = "1";
  }

  /** Advances the fade. Frame seconds, like {@link DialogueLine.update}. */
  update(frameSeconds: number): void {
    if (this.shown === null) {
      return;
    }

    this.remaining -= frameSeconds;

    if (this.remaining <= 0) {
      this.shown = null;
      this.element.hidden = true;
      this.element.textContent = "";

      return;
    }

    if (this.remaining < FEEDBACK_FADE_SECONDS) {
      this.element.style.opacity = (this.remaining / FEEDBACK_FADE_SECONDS).toFixed(3);
    }
  }
}
